import { useAsyncOperation } from "@core/hooks/use-async-operation";
import { useI18n } from "@core/i18n/i18n";

import { intlLocaleToLocale } from "@core/i18n/locale-helper";
import { onfidoService, userService } from "@core/service-container";
import { OnfidoSDK } from "@typings/onfido-typing";
import { Handle } from "onfido-sdk-ui/types/Onfido";

interface UseRegistrationConfigHook {
	initOnfido: (onComplete: () => void) => Promise<Handle | undefined>;
	loading: boolean;
	error: boolean;
}	

export const OnfidoComponentId = "onfido-mount";

const languageRestriction = (language: string): string => {
	const spanishCountries = ["es_CL", "es_UY", "es_AR"];
	const englishCountries = ["en_CA"];
	const swissCountries = ["fr_CH", "de_CH", "it_CH"];
	
	if (spanishCountries.includes(language)) {
		return "es_419";
	} else if (englishCountries.includes(language)) {
		return "en_US";
	} else if (swissCountries.includes(language)) {
		const mapping: { [key: string]: string } = {
			"fr_CH": "fr",
			"de_CH": "de",
			"it_CH": "it",
		};
		return mapping[language] || language;
	} else {
		return language;
	}
};

function loadOnfidoScript(): Promise<OnfidoSDK> {
	return new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.src = "https://sdk.onfido.com/v14";
		script.async = true;
		script.onload = () => resolve(window.Onfido);
		script.onerror = () => reject(new Error("[Onfido] Failed to load SDK"));
		document.head.appendChild(script);
	  });
}

export const useOnfido = (): UseRegistrationConfigHook => {
	const { locale } = useI18n();
  
	const [initOnfido, loading, error] = useAsyncOperation(
		async (onComplete: () => void): Promise<Handle> => {
			const onfidoUserConfig = await onfidoService.getOnfidoInitConfig();

			const onfido = await loadOnfidoScript();

			const onfidoOut = onfido.init({
				token: onfidoUserConfig.token,
				workflowRunId: onfidoUserConfig.workflowRunId,
				containerId: OnfidoComponentId,
				onComplete: async () => {
					await userService.completeIdentityVerification();
					onComplete();
					await onfidoOut.tearDown();
				},
				onError: () => { onfidoOut.tearDown().finally(() => { throw error; }); },
				language: {
					locale: languageRestriction(intlLocaleToLocale(locale)),
					phrases: {},
				},
			});
			return onfidoOut;
		},
		{ deps: [locale] },
	);
  
	return { initOnfido, loading, error };
  };